<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { EntityListPage, type EntityConfig } from '@botto/core';
import { managersStore, getPartnersLookup, getManagersLookup } from '../../api/adapters/accounts';
import type { Manager } from '../../api';

const listRef = ref<InstanceType<typeof EntityListPage> | null>(null);
const partnersLookup = getPartnersLookup();
const managersLookup = getManagersLookup();

onMounted(() => {
  void partnersLookup.ensureLoaded();
});

function updateGridDimensions() {
  listRef.value?.updateGridDimensions();
}

function partnersLabel(ids: number[] | null | undefined): string {
  if (!ids?.length) return '—';
  return ids.map((id) => partnersLookup.labelOf(id)).join(', ');
}

async function onManagersChanged() {
  await managersLookup.reload();
}

defineExpose({ updateGridDimensions });

const config = computed<EntityConfig<Manager>>(() => ({
  title: 'Менеджер',
  titlePlural: 'Менеджеры',
  popupWidth: 560,
  popupHeight: 520,
  columns: [
    { dataField: 'id', caption: 'ID', dataType: 'number', width: 80 },
    { dataField: 'name', caption: 'Имя' },
    { dataField: 'phone', caption: 'Телефон', width: 140 },
    { dataField: 'email', caption: 'Email', width: 180 },
    {
      dataField: 'partner_ids',
      caption: 'Партнёры',
      width: 240,
      allowSorting: false,
      calculateDisplayValue: (row) => partnersLabel(row.partner_ids),
    },
    { dataField: 'can_change_partner', caption: 'Смена партнёра', dataType: 'boolean', width: 130 },
    { dataField: 'archived', caption: 'Архив', dataType: 'boolean', width: 80 },
  ],
  fields: [
    { dataField: 'name', label: 'Имя', required: true, colSpan: 2 },
    { dataField: 'phone', label: 'Телефон', required: true },
    { dataField: 'email', label: 'Email', createOnly: true },
    {
      dataField: 'password',
      label: 'Password',
      createOnly: true,
      editorOptions: { mode: 'password' },
    },
    {
      dataField: 'partner_ids',
      label: 'Партнёры',
      colSpan: 2,
      lookup: partnersLookup,
      lookupMultiple: true,
    },
    { dataField: 'can_change_partner', label: 'Может менять партнёра', editorType: 'dxCheckBox' },
    { dataField: 'archived', label: 'Архив', editorType: 'dxCheckBox' },
  ],
}));
</script>

<template>
  <EntityListPage
    ref="listRef"
    embedded
    :config="config"
    :store="managersStore"
    @created="onManagersChanged"
    @updated="onManagersChanged"
    @deleted="onManagersChanged"
  />
</template>
